// src/components/staff/mobile/MobileStaffHome.jsx
import React, { useState, useEffect } from 'react';
import {
  Fuel, Wrench, CreditCard, Calendar, Users, Clock,
  AlertCircle, CheckCircle, TrendingUp, Warehouse, LogOut,
} from 'lucide-react';import { useAuth } from '../../../contexts/AuthContext';
import { getAllClientRequests, getAllInvoices, getStaffList } from '../../../services/staffApi';

const GLOBAL_STYLE = `@keyframes spin { to { transform: rotate(360deg) } }`;

// ─── Role badge config ────────────────────────────────────────────────────────
const getRoleInfo = (level) => {
  if (level >= 110) return { label: 'Super Admin',  color: '#f87171' };
  if (level >= 105) return { label: 'Sys Admin',    color: '#c084fc' };
  if (level >= 100) return { label: 'Administrator',color: '#f87171' };
  if (level >= 90)  return { label: 'Manager',      color: '#fb923c' };
  if (level >= 75)  return { label: 'Supervisor',   color: '#facc15' };
  if (level >= 30)  return { label: 'Line Crew',    color: '#34d399' };
  if (level >= 25)  return { label: 'Dispatcher',   color: '#60a5fa' };
  if (level >= 20)  return { label: 'Accounting',   color: '#a78bfa' };
  return { label: 'Staff', color: '#94a3b8' };
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, Icon, accent, onClick }) => (
  <button onClick={onClick} style={{
    background: '#1e293b', borderRadius: 18, border: '0.5px solid #334155',
    padding: '14px 12px', textAlign: 'left',
    cursor: onClick ? 'pointer' : 'default',
    display: 'flex', flexDirection: 'column', gap: 8, width: '100%',
    transition: 'transform 0.12s',
  }}
    onTouchStart={e => onClick && (e.currentTarget.style.transform = 'scale(0.96)')}
    onTouchEnd={e => (e.currentTarget.style.transform = 'scale(1)')}
  >
    <div style={{ width: 34, height: 34, borderRadius: 10, background: `${accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={17} color={accent} />
    </div>
    <div>
      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent, lineHeight: 1 }}>
        {value ?? <span style={{ fontSize: 14 }}>…</span>}
      </div>
    </div>
  </button>
);

const SectionTitle = ({ text }) => (
  <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
    {text}
  </div>
);

// ─── Recent Request Item ──────────────────────────────────────────────────────
const RecentItem = ({ req }) => {
  const isFuel = req.request_type === 'fuel';
  const accent = isFuel ? '#fb923c' : '#f87171';
  const Icon   = isFuel ? Fuel : Wrench;
  const STATUS = {
    pending:   { color: '#eab308', label: 'Pending'   },
    approved:  { color: '#22c55e', label: 'Approved'  },
    completed: { color: '#3b82f6', label: 'Completed' },
    rejected:  { color: '#ef4444', label: 'Rejected'  },
  };
  const s = STATUS[req.status] || STATUS.pending;

  return (
    <div style={{ background: '#1e293b', borderRadius: 16, border: '0.5px solid #334155', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 38, height: 38, borderRadius: 11, background: `${accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={17} color={accent} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {isFuel ? `${req.quantity || '?'} gal — ${req.tail_number || ''}` : (req.tail_number || 'Maintenance')}
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
          {req.client_first_name} {req.client_last_name}
          {req.company_name ? ` · ${req.company_name}` : ''}
        </div>
      </div>
      <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: `${s.color}18`, color: s.color, flexShrink: 0 }}>
        {s.label}
      </span>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const MobileStaffHome = ({ onNavigate }) => {
  const { user, logout, getUserLevel, hasMinLevel } = useAuth();
  const [stats, setStats]   = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  const level    = getUserLevel();
  const roleInfo = getRoleInfo(level);
  const initials = `${user?.first_name?.[0] ?? ''}${user?.last_name?.[0] ?? ''}`.toUpperCase();
  const hour     = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  useEffect(() => {
    const load = async () => {
      try {
        // Charger les vraies données depuis les routes confirmées
        const [reqRes, invRes] = await Promise.all([
          getAllClientRequests(),
          hasMinLevel(20) ? getAllInvoices() : Promise.resolve({ data: [] }),
        ]);

        const requests  = reqRes.data?.requests || [];
        const invoices  = Array.isArray(invRes.data) ? invRes.data : (invRes.data?.invoices || []);

        setStats({
          pending_requests:  requests.filter(r => r.status === 'pending').length,
          approved_requests: requests.filter(r => r.status === 'approved').length,
          pending_invoices:  invoices.filter(i => i.status === 'pending').length,
          fuel_requests:     requests.filter(r => r.request_type === 'fuel' && r.status === 'pending').length,
          maintenance:       requests.filter(r => r.request_type === 'maintenance' && r.status === 'pending').length,
        });

        // 3 demandes les plus récentes
        setRecent(requests.slice(0, 3));
      } catch (e) {
        console.error('Home stats error:', e);
        setStats({ pending_requests: 0, approved_requests: 0, pending_invoices: 0, fuel_requests: 0, maintenance: 0 });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div style={{ padding: '0 16px 100px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{GLOBAL_STYLE}</style>

      {/* Hero banner */}
      <div style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)', borderRadius: 22, padding: '18px 18px 16px', border: '0.5px solid #2d4a6a' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 12, color: '#93c5fd', marginBottom: 2 }}>{greeting} 👋</div>
            <div style={{ fontSize: 19, fontWeight: 700, color: '#fff' }}>
              {user?.first_name} {user?.last_name}
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', marginTop: 5, padding: '3px 10px', borderRadius: 20, background: `${roleInfo.color}22`, border: `0.5px solid ${roleInfo.color}44` }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: roleInfo.color }}>{roleInfo.label}</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            {/* Avatar */}
            <div style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: '#fff' }}>
              {initials}
            </div>
            {/* Bouton déconnexion */}
            <button
              onClick={logout}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: 'rgba(239,68,68,0.15)', border: '0.5px solid rgba(239,68,68,0.3)', borderRadius: 10, cursor: 'pointer' }}
            >
              <LogOut size={12} color="#f87171" />
              <span style={{ fontSize: 11, color: '#f87171', fontWeight: 500 }}>Sign out</span>
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 11px', background: 'rgba(255,255,255,0.07)', borderRadius: 10 }}>
          <Clock size={12} color="#93c5fd" />
          <span style={{ fontSize: 11, color: '#93c5fd' }}>
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </span>
        </div>
      </div>

      {/* Stats — adaptées au rôle */}
      <div>
        <SectionTitle text="Overview" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {/* Tous les rôles ops (25+) voient les requests */}
          {hasMinLevel(25) && (
            <>
              <StatCard label="Pending Requests" value={loading ? null : stats?.pending_requests} Icon={AlertCircle} accent="#eab308" onClick={() => onNavigate('requests')} />
              <StatCard label="Fuel Pending"     value={loading ? null : stats?.fuel_requests}    Icon={Fuel}        accent="#fb923c" onClick={() => onNavigate('requests')} />
              <StatCard label="Maintenance"      value={loading ? null : stats?.maintenance}       Icon={Wrench}      accent="#f87171" onClick={() => onNavigate('requests')} />
              <StatCard label="Approved"         value={loading ? null : stats?.approved_requests} Icon={CheckCircle} accent="#22c55e" onClick={() => onNavigate('requests')} />
            </>
          )}
          {/* Finance — Accountant/Cashier (20-24) */}
          {hasMinLevel(20) && !hasMinLevel(25) && (
            <>
              <StatCard label="Pending Invoices" value={loading ? null : stats?.pending_invoices} Icon={CreditCard}  accent="#facc15" onClick={() => onNavigate('invoices')} />
              <StatCard label="Today's Payments" value={loading ? null : 0}                       Icon={CheckCircle} accent="#22c55e" onClick={() => onNavigate('invoices')} />
            </>
          )}
          {/* Admin/Manager (90+) voient aussi les invoices */}
          {hasMinLevel(90) && (
            <StatCard label="Pending Invoices" value={loading ? null : stats?.pending_invoices} Icon={CreditCard} accent="#facc15" onClick={() => onNavigate('invoices')} />
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div>
        <SectionTitle text="Quick Actions" />
        <div style={{ display: 'flex', gap: 8 }}>
          {hasMinLevel(25) && (
            <button onClick={() => onNavigate('requests')} style={{ flex: 1, background: '#1e293b', border: '0.5px solid #334155', borderRadius: 16, padding: '12px 6px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(249,115,22,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Fuel size={18} color="#fb923c" />
              </div>
              <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>Requests</span>
            </button>
          )}
          {hasMinLevel(25) && (
            <button onClick={() => onNavigate('queue')} style={{ flex: 1, background: '#1e293b', border: '0.5px solid #334155', borderRadius: 16, padding: '12px 6px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Wrench size={18} color="#f87171" />
              </div>
              <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>Queue</span>
            </button>
          )}
          {hasMinLevel(25) && (
            <button onClick={() => onNavigate('chat')} style={{ flex: 1, background: '#1e293b', border: '0.5px solid #334155', borderRadius: 16, padding: '12px 6px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(52,211,153,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Users size={18} color="#34d399" />
              </div>
              <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>Chat</span>
            </button>
          )}
          {hasMinLevel(20) && (
            <button onClick={() => onNavigate('invoices')} style={{ flex: 1, background: '#1e293b', border: '0.5px solid #334155', borderRadius: 16, padding: '12px 6px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(234,179,8,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CreditCard size={18} color="#facc15" />
              </div>
              <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>Finance</span>
            </button>
          )}
          {hasMinLevel(20) && (
            <button onClick={() => onNavigate('schedule')} style={{ flex: 1, background: '#1e293b', border: '0.5px solid #334155', borderRadius: 16, padding: '12px 6px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(96,165,250,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Calendar size={18} color="#60a5fa" />
              </div>
              <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>Schedule</span>
            </button>
          )}
        </div>
      </div>

      {/* Recent activity */}
      {hasMinLevel(25) && recent.length > 0 && (
        <div>
          <SectionTitle text="Recent Requests" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {recent.map(req => <RecentItem key={req.id} req={req} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileStaffHome;